import { FC, useMemo, useState } from 'react'
import { alpha, Box, ButtonBase, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import { ArrowBack, CheckCircle, ErrorOutline, ReceiptOutlined, ScheduleOutlined } from '@mui/icons-material'
import { useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'

import { useActiveLeaseContractId } from 'services/contracts/hooks/useActiveLeaseContractId'
import { useSchedulesWithDocumnets } from 'services/contracts/hooks/useSchedulesWithDocumnets'
import { useLeaseContractsList } from 'services/contracts/hooks/useLeaseContracts'
import { useLanguage } from 'services/i18n/i18n.hooks'
import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import HelpdeskEmptyState from 'components/Helpdesk/HelpdeskEmptyState'
import theme from 'styles/theme'

import AdditionalServicesPaymentStatus from './AdditionalServicesPaymentStatus'
import PaymentScheduleDocumnet from './PaymentScheduleDocumnet'
import {
  flattenScheduleGroups,
  formatMoney,
  getDocumentAmount,
  getDocumentDueDate,
  getDocumentPaidAmount
} from './leases.utils'

type FilterValue = 'all' | 'paid' | 'toPay'

const TO_PAY_COLOR = '#D4A800'
const TABLE_SHADOW = '0px 1px 3px rgba(0, 0, 0, 0.06)'

const documentKey = (document: { type?: string; id: number }) => `${document.type}-${document.id}`

const capitalize = (value: string) =>
  value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value

// Payment schedule ("Panel Klienta 3.0": Figma "Umowy — Harmonogram płatności").
const AdditionalServicesPaymentSchedule: FC = () => {
  const { formatMessage } = useIntl()
  const muiTheme = useTheme()
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'))
  const navigate = useNavigate()
  const { id } = useParams()
  const { dateAdapter } = useLanguage()
  const [filter, setFilter] = useState<FilterValue>('all')
  const { activeLeaseContractId } = useActiveLeaseContractId()

  const unpaid = useSchedulesWithDocumnets({
    activeLeaseContractId,
    isPaid: false
  })
  const paid = useSchedulesWithDocumnets({
    activeLeaseContractId,
    isPaid: true
  })

  const { getActiveContract } = useLeaseContractsList()

  const activeContract = useMemo(
    () => getActiveContract(activeLeaseContractId),
    [activeLeaseContractId, getActiveContract]
  )

  const documents = useMemo(() => {
    const unpaidDocuments = flattenScheduleGroups(unpaid.schedulesWithDocumnets)
    const paidDocuments = flattenScheduleGroups(paid.schedulesWithDocumnets)

    const mergeUnique = (list: typeof unpaidDocuments) => {
      const map = new Map<string, (typeof unpaidDocuments)[number]>()
      list.forEach((document) => map.set(documentKey(document), document))
      return [...map.values()].sort((a, b) =>
        a.displayDate.isAfter(b.displayDate) ? 1 : -1
      )
    }

    if (filter === 'paid') {
      return mergeUnique(paidDocuments)
    }

    if (filter === 'toPay') {
      return mergeUnique(unpaidDocuments)
    }

    return mergeUnique([...unpaidDocuments, ...paidDocuments])
  }, [filter, paid.schedulesWithDocumnets, unpaid.schedulesWithDocumnets])

  const isLoading = unpaid.isLoading || paid.isLoading
  const currency = activeContract?.paymentCurrency ?? ''

  const filters: { value: FilterValue; label: string }[] = [
    {
      value: 'all',
      label: formatMessage({
        id: 'additionalServicesPaymentSchedule.all',
        defaultMessage: 'Wszystkie'
      })
    },
    {
      value: 'paid',
      label: formatMessage({
        id: 'additionalServicesPaymentSchedule.paid',
        defaultMessage: 'Opłacone'
      })
    },
    {
      value: 'toPay',
      label: formatMessage({
        id: 'additionalServicesPaymentSchedule.toPay',
        defaultMessage: 'Do zapłaty'
      })
    }
  ]

  const openDocument = (document: (typeof documents)[number]) => {
    if (!document.number || !document.type) {
      return
    }

    navigate(`/additional-services/${id}/payment-schedule/${document.type}/${document.id}`)
  }

  const getStatus = (document: (typeof documents)[number]) => {
    if (!document.number) {
      return {
        label: formatMessage({ id: 'paymentSchedule.statusAwaiting', defaultMessage: 'Oczekująca' }),
        color: theme.r4r.grey,
        backgroundColor: theme.r4r.greyLight,
        Icon: ScheduleOutlined
      }
    }

    if (document.isPaid || filter === 'paid') {
      return {
        label: formatMessage({ id: 'paymentSchedule.statusPaid', defaultMessage: 'Opłacona' }),
        color: theme.r4r.green,
        backgroundColor: alpha(theme.r4r.green, 0.1),
        Icon: CheckCircle
      }
    }

    return {
      label: formatMessage({ id: 'paymentSchedule.statusToPay', defaultMessage: 'Do zapłaty' }),
      color: TO_PAY_COLOR,
      backgroundColor: alpha(theme.r4r.yellow, 0.15),
      Icon: ErrorOutline
    }
  }

  const header = isDesktop ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '20px' }}>
      <IconButton
        onClick={() => navigate(`/additional-services/${id}`)}
        sx={{ width: '36px', height: '36px', color: theme.r4r.navy, borderRadius: 0 }}>
        <ArrowBack sx={{ fontSize: '22px' }} />
      </IconButton>
      <Typography
        sx={{ fontSize: '24px', fontWeight: 700, lineHeight: '28.8px', color: theme.r4r.navy }}>
        {formatMessage({
          id: 'additionalServicesPaymentSchedule.schedule',
          defaultMessage: 'Harmonogram płatności'
        })}
      </Typography>
    </Box>
  ) : null

  const tableHeaderCellSx = {
    fontSize: '11px',
    fontWeight: 700,
    color: theme.r4r.grey,
    letterSpacing: '0.5px',
    textTransform: 'uppercase'
  } as const

  const content = (
    <>
      {header}
      <AdditionalServicesPaymentStatus />
      <Box sx={{ display: 'flex', gap: '8px', mb: '20px', flexWrap: 'wrap' }}>
        {filters.map(({ value, label }) => {
          const isSelected = value === filter
          return (
            <ButtonBase
              key={value}
              onClick={() => setFilter(value)}
              sx={{
                padding: '8px 16px',
                fontSize: '12px',
                fontWeight: 600,
                fontFamily: muiTheme.typography.fontFamily,
                whiteSpace: 'nowrap',
                backgroundColor: isSelected ? alpha(theme.r4r.navy, 0.08) : '#fff',
                color: isSelected ? theme.r4r.navy : theme.r4r.grey,
                border: `1px solid ${isSelected ? theme.r4r.navy : theme.r4r.greyLight}`
              }}>
              {label}
            </ButtonBase>
          )
        })}
      </Box>

      {!documents.length && !isLoading && (
        <HelpdeskEmptyState
          Icon={ReceiptOutlined}
          title={formatMessage({
            id: 'additionalServicesPaymentSchedule.noDocuments',
            defaultMessage: 'Brak dokumentów.'
          })}
        />
      )}

      {!!documents.length && isDesktop && (
        <Box sx={{ backgroundColor: '#fff', boxShadow: TABLE_SHADOW }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 160px 160px 140px',
              gap: '12px',
              backgroundColor: '#f9fafb',
              padding: '14px 20px',
              alignItems: 'center'
            }}>
            <Typography sx={tableHeaderCellSx}>
              {formatMessage({
                id: 'additionalServicesPaymentSchedule.month',
                defaultMessage: 'Miesiąc'
              })}
            </Typography>
            <Typography sx={tableHeaderCellSx}>
              {formatMessage({
                id: 'additionalServicesPaymentSchedule.term',
                defaultMessage: 'Termin'
              })}
            </Typography>
            <Typography sx={{ ...tableHeaderCellSx, textAlign: 'right' }}>
              {formatMessage({
                id: 'additionalServicesPaymentSchedule.amount',
                defaultMessage: 'Kwota'
              })}
            </Typography>
            <Typography sx={{ ...tableHeaderCellSx, textAlign: 'right' }}>
              {formatMessage({
                id: 'additionalServicesPaymentSchedule.status',
                defaultMessage: 'Status'
              })}
            </Typography>
          </Box>
          {documents.map((document) => {
            const status = getStatus(document)
            const dueDate = getDocumentDueDate(document)
            const clickable = !!document.number
            const monthLabel = capitalize(
              document.displayDate.locale(dateAdapter).format('MMMM YYYY')
            )

            return (
              <Box
                key={documentKey(document)}
                onClick={() => clickable && openDocument(document)}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 160px 160px 140px',
                  gap: '12px',
                  padding: '14px 20px',
                  alignItems: 'center',
                  minHeight: '49px',
                  borderBottom: `1px solid ${theme.r4r.greyLight}`,
                  cursor: clickable ? 'pointer' : 'default',
                  '&:last-of-type': { borderBottom: 'none' },
                  '&:hover': clickable ? { backgroundColor: alpha(theme.r4r.navy, 0.02) } : undefined
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                  <status.Icon sx={{ fontSize: '18px', color: status.color, flexShrink: 0 }} />
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: theme.r4r.ink,
                      textTransform: 'capitalize'
                    }}>
                    {monthLabel}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '13px', color: theme.r4r.ink }}>
                  {dueDate.isValid() ? dueDate.format('DD.MM.YYYY') : '—'}
                </Typography>
                <Typography
                  sx={{ fontSize: '13px', fontWeight: 700, color: theme.r4r.ink, textAlign: 'right' }}>
                  {formatMoney(getDocumentAmount(document), currency)}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Box sx={{ backgroundColor: status.backgroundColor, padding: '3px 10px' }}>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: status.color,
                        whiteSpace: 'nowrap'
                      }}>
                      {status.label}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )
          })}
        </Box>
      )}

      {!!documents.length && !isDesktop && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {documents.map((document) => {
            const dueDate = getDocumentDueDate(document)
            const clickable = !!document.number

            return (
              <PaymentScheduleDocumnet
                key={documentKey(document)}
                data={document}
                documentId={document.id}
                type={document.type}
                amount={getDocumentAmount(document)}
                number={document.number}
                file={document.file}
                currency={currency}
                isPaid={document.isPaid || filter === 'paid'}
                paidAmount={getDocumentPaidAmount(document)}
                monthLabel={capitalize(
                  document.displayDate.locale(dateAdapter).format('MMMM YYYY')
                )}
                dueDateLabel={dueDate.isValid() ? dueDate.format('DD.MM.YYYY') : '—'}
                onOpen={clickable ? () => openDocument(document) : undefined}
              />
            )
          })}
        </Box>
      )}
    </>
  )

  if (isDesktop) {
    return content
  }

  return (
    <>
      <BackNavigationHeader
        label={formatMessage({
          id: 'additionalServicesPaymentSchedule.schedule',
          defaultMessage: 'Harmonogram płatności'
        })}
        navigateTo={`/additional-services/${id}`}
      />
      <BackNavigationContent>
        <Box pt='env(safe-area-inset-top)'>{content}</Box>
      </BackNavigationContent>
    </>
  )
}

export default AdditionalServicesPaymentSchedule
